import {useMutation, useQuery, useQueryClient} from "react-query";
import axios from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {useSnackbar} from "notistack";
import {
    InjectedEnergyLossParameter,
    InjectedEnergyLossParametersResponse
} from "../../../Shared/Types/InjectedEnergyLossParameter";

const apiUrl = "api/power-station/details";
const apiExpectedDataUrl = "api/expected-data";
const apiPowerStationEnabled = "/api/power-station/find-all-enabled";
const apiPowerStationBasicList = "/api/power-station/list";
const apiTeleObjectsByEquipmentId =  "api/teleobject-config/find-all-by-equipment-id"
// Endpoints exclusivos do cadastro de usina: não filtram usina habilitada (exibem/editam usina desabilitada)
const apiPowerStationForm = "/api/power-station-form";
const apiTeleObjectsForGridForm = `${apiPowerStationForm}/teleobject/grid`;
const apiTeleObjectMap = "/api/teleobject-map";
const apiTeleObjectMapItem = "/api/teleobject-map-item";

interface ResponseConfigurationEntity {
    message: string;
    data: {
        id: number,
        name: string,
        value: number
    }
}

interface   ResponseConfigurationUserAssociation {
    message: string;
    data: [{
        id: number,
        name: string,
        email: string,
        hasPermission: boolean
    }]
}

const fetchAllStates = async () => {
    try {
        return await axios
            .get(`api/state`)
            .then((res) => res.data.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

const fetchAllCitiesByStateId = async (id: number) => {
    try {
        return await axios
            .get(`api/cities?state=${id}`)
            .then((res) => res.data.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

export const FindAllStatesReactQuery = () => {
    return useQuery({
        queryKey: ["data-find-all-states"],
        queryFn: () => {
            return fetchAllStates();
        }
    });
}

export const FindAllCitiesByStateId = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return fetchAllCitiesByStateId(id);
        },
        onError: (error: any) => {
            enqueueSnackbar(`Cidades não encontradas: ${error.message}`, { variant: "error" });
        }
    })
}

const fetchPowerStations = async (pagedSearchParams: PagedSearchParams) => {
    try {
        return await axios
            .get(`${apiUrl}/search`, {
                params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null
            })
            .then((res) => res.data.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

const fetchExpectedData = async (pagedSearchParams: PagedSearchParams) => {
    try {
        return await axios
            .get(`${apiExpectedDataUrl}/search`, {
                params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null
            })
            .then((res) => res.data.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

export const PowerStationSearchReactQuery = (pagedSearchParams: PagedSearchParams) => {
    return useQuery({
        queryKey: ["data", pagedSearchParams],
        queryFn: () => {
            return fetchPowerStations(pagedSearchParams);
        }
    });
};

export const ExpectedDataSearchReactQuery = (pagedSearchParams: PagedSearchParams, enable: boolean) => {
    return useQuery({
        queryKey: ["page expectedData", pagedSearchParams],
        queryFn: () => {
            return fetchExpectedData(pagedSearchParams);
        },
        enabled: enable
    });
};

const fetchPowerStationById = async (id: number) => {
    try {
        return await axios.get(`${apiUrl}/${id}`).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

export const FindPowerStationByIdReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return fetchPowerStationById(id);
        },
        onError: (error: any) => {
            enqueueSnackbar(`Erro ao carregar a powerStation: ${error.message}`, { variant: "error" });
        }
    })
};

export const FindNumerOfEquipmentBySkidReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return fetchNumberOfEquipmentBySkid(id);
        },
        onError: (error: any) => {
            enqueueSnackbar(`Erro ao carregar a powerStation: ${error.message}`, { variant: "error" });
        }
    })
};

const deletePowerStation = async (id: number) => {
    try {
        return await axios.delete(`api/power-station/${id}`).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

const fetchNumberOfEquipmentBySkid  = async (id: number) => {
    try {
        return await axios.get(`${apiPowerStationForm}/skid/number-of-equipment/${id}`).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
}

export const DeletePowerStationReactQuery = (pagedSearchParams: PagedSearchParams) => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) => {
            return deletePowerStation(data.id);
        },
        onSuccess: () => {
            enqueueSnackbar("Usina excluída com sucesso!", { variant: "success" });
            return queryClient.invalidateQueries({
                queryKey: ["data", pagedSearchParams]
            });
        },
        onError: (error: any) => {
            const status = error.response.data.status
            if(status === 409){
                enqueueSnackbar(`Existem dados vinculados a essa usina, por favor contactar o administrador do sistema.`, { variant: "error" });
            }else{
                enqueueSnackbar(`Erro ao excluir usina: ${error.message}`, { variant: "error" });
            }

        }
    })
};

const fetchAllEnabledPowerStations = async () => {
    try {
        return await axios
            .get(apiPowerStationEnabled)
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
};

export const FindAllEnabledPowerStationsReactQuery = () => {
    return useQuery({
        queryKey: ["data-find-all-enabled-powerStations"],
        queryFn: fetchAllEnabledPowerStations
    });
};

export interface PowerStationBasic {
    id: number;
    name: string;
}

const fetchPowerStationBasicList = async (): Promise<PowerStationBasic[]> => {
    try {
        const res = await axios.get<PowerStationBasic[]>(apiPowerStationBasicList);
        return res.data ?? [];
    } catch (error) {
        console.error("Error fetching power station basic list:", error);
        throw error;
    }
};

export const FindAllPowerStationsBasicReactQuery = () => {
    return useQuery({
        queryKey: ["data-find-all-power-stations-basic"],
        queryFn: fetchPowerStationBasicList,
        staleTime: 5 * 60 * 1000,
    });
};

const fetchTeleObjectsForGridForm = async (powerStationId: number, pagedSearchParams: PagedSearchParams) => {
    try {
        const urlParams = new URLSearchParams(pagedSearchParams.toURLSearchParams());
        if(powerStationId !== undefined){
            urlParams.append('powerStationId', powerStationId.toString());
        }
        return await axios
            .get(apiTeleObjectsForGridForm, {
                params: urlParams
            })
            .then((res) => res.data); // Ajuste conforme a estrutura da sua resposta
    } catch (error) {
        console.error("Erro ao buscar os dados:", error);
        throw error;
    }
};

export const FindAllTeleObjectsByPowerStationIdReactQuery = (powerStationId: number | null, pagedSearchParams: PagedSearchParams) => {
    return useQuery({
        queryKey: ["data-find-all-teleObjects-powerStations", powerStationId, pagedSearchParams],
        queryFn: () => {
            if (powerStationId !== null) {
                return fetchTeleObjectsForGridForm(powerStationId, pagedSearchParams);
            }
        },
        enabled: powerStationId !== null, // Desabilita a query se o powerStationId for null
    });
};

const fetchAllTeleObjectsConfigByEquipmentId = async (equipmentId: number) => {
    try {
        const response = await axios.get(`${apiTeleObjectsByEquipmentId}/${equipmentId}`);
        return response.data.data; // Retorna os dados dentro da chave `data`
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error; // Lança o erro para ser tratado onde a função for chamada
    }
};

const SearchTeleObjectMap  = async (pagedSearchParams: PagedSearchParams, powerStationId: number) => {
    try {
        return await axios.get(`${apiTeleObjectMap}/search?powerStationId=${powerStationId}`, {
            params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null
        })
        .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
}

const SearchTeleObjectMapItem = async (pagedSearchParams: PagedSearchParams, powerStationId: number) => {
    try {
        return await axios.get(`${apiTeleObjectMapItem}/search?powerStationId=${powerStationId}`, {
            params: pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null
        })
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
}

export const TeleObjectMapSearchReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        
        mutationFn: ({pagedSearchParams, powerStationId}: {pagedSearchParams: PagedSearchParams, powerStationId: number}) => {
            return SearchTeleObjectMap(pagedSearchParams, powerStationId) // Ajuste conforme a estrutura da sua resposta
        },
        onError: (error: any) => {
            console.log("Erro ao carregar o mapeamento de teleobjetos:" + error.message);
            enqueueSnackbar(`Não foi possível carregar o mapeamento de teleobjetos. Por favor tente novamente ou entre em contato com o suporte.`, { variant: "error" });
        },
        retryDelay: 5000, // Tempo de espera entre as tentativas de retry (5 segundos)
        retry: 2 // Número máximo de tentativas de retry
    })
};

export const TeleObjectMapItemSearchReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({

        mutationFn: ({pagedSearchParams, powerStationId}: {pagedSearchParams: PagedSearchParams, powerStationId: number}) => {
            return SearchTeleObjectMapItem(pagedSearchParams, powerStationId)
        },
        onError: (error: any) => {
            console.log("Erro ao carregar o mapeamento de inversores e trackers:" + error.message);
            enqueueSnackbar(`Não foi possível carregar o mapeamento de inversores e trackers. Por favor tente novamente ou entre em contato com o suporte.`, { variant: "error" });
        },
        retryDelay: 5000,
        retry: 2
    })
};

const deleteTeleObjectMap = async (id: number) => {
    try {
        return await axios.delete(`${apiTeleObjectMap}/${id}`).then((res) => res.data);
    } catch (error) {
        console.error("Error deleting teleObjectMap:", error);
        throw error;
    }
}

export const TeleObjectMapDeleteReactQuery = (pagedSearchParams: PagedSearchParams, powerStationId: number | null) => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => {
            return deleteTeleObjectMap(id);
        },
        onSuccess: () => {
            if (powerStationId !== null) {
                // refresh the search results
                return queryClient.invalidateQueries({ queryKey: ["data-find-all-teleObjects-powerStations", powerStationId, pagedSearchParams] });
            }
        },
        onError: (error: any) => {
            console.log("Erro ao excluir mapeamento:", error?.message ?? error);
            enqueueSnackbar(`Erro ao excluir mapeamento de teleobjetos. Por favor tente novamente ou entre em contato com o suporte.`, { variant: "error" });
        }
    })
}

const saveTeleObjectMap = async (payload: any) => {
    if (payload.id != null) {
        try {
            return await axios.put(`${apiTeleObjectMap}`, payload).then((res) => res.data);
        } catch (error) {
            console.error("Error updating teleObjectMap:", error);
            throw error;
        }
    }
    try {
        return await axios.post(`${apiTeleObjectMap}`, payload).then((res) => res.data);
    } catch (error) {
        console.error("Error saving teleObjectMap:", error);
        throw error;
    }
}

export const TeleObjectMapSaveReactQuery = (powerStationId: number | null) => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: any) => {
            return saveTeleObjectMap(payload);
        },
        onSuccess: () => {
            enqueueSnackbar("Mapeamento salvo com sucesso!", { variant: "success" });
            // invalidate related queries for this power station
            queryClient.invalidateQueries({
                predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === "data-find-all-teleObjects-powerStations" && query.queryKey[1] === powerStationId
            });
        },
        onError: (error: any) => {
            console.log("Erro ao salvar mapeamento tes:", error);
            const status = error?.response?.status ?? error?.response?.data?.status;
            if (status === 409) {
                const detail = error?.response?.data?.message || 'Já existe um mapeamento com esse Tracker.';
                enqueueSnackbar(`Não foi possível salvar: ${detail}`, { variant: "error" });
            } else {
                enqueueSnackbar(`Erro ao salvar o mapeamento. Por favor tente novamente ou entre em contato com o suporte.`, { variant: "error" });
            }
        }
    })
}

const SearchTeleObjectMapTracker  = async (powerStationId: number) => {
    try {
        return await axios.get(`${apiTeleObjectMap}/available-tele-object-trackers/${powerStationId}`)
        .then((res) => {        
            return res.data;
        });
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
}

export const TeleObjectMapTrackerReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return SearchTeleObjectMapTracker(id) // Ajuste conforme a estrutura da sua resposta
        },
        onError: (error: any) => {
            console.error("Erro ao carregar os trackeadores disponíveis:", error);
            enqueueSnackbar(`Erro ao carregar os trackeadores disponíveis. Por favor tente novamente ou entre em contato com o suporte.`, { variant: "error" });
        }
    })
};


const SearchTeleObjectMapCurrents  = async (powerStationId: number) => {
    try {
        return await axios.get(`${apiTeleObjectMap}/available-tele-object-currents/${powerStationId}`)
        .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
    }
}

export const TeleObjectMapCurrentsReactQuery = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return SearchTeleObjectMapCurrents(id) // Ajuste conforme a estrutura da sua resposta
        },
        onError: (error: any) => {
            console.error("Erro ao carregar os correntes disponíveis:", error);
            enqueueSnackbar(`Erro ao carregar os correntes disponíveis. Por favor tente novamente ou entre em contato com o suporte.`, { variant: "error" });
        }
    })
};


const fetchConfiguration = async (teleObjectMapMaxNumber: string): Promise<ResponseConfigurationEntity> => {
    try {
        return await axios
            .get<ResponseConfigurationEntity>(`/api/configuration/${teleObjectMapMaxNumber}`,{timeout: 30000})
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching configuration by name:", error);
        return Promise.reject(error);
    }
}

export const ConfigurationByNameReactQuery = (teleObjectMapMaxNumber: any) => {
    return useQuery({
        queryKey: ['data', teleObjectMapMaxNumber],
        queryFn: () => {
           return fetchConfiguration(teleObjectMapMaxNumber)
        },
        enabled: !!teleObjectMapMaxNumber
    })
}

const fetchAllTeleObjectsConfigByEquipmentIdForPowerStationForm = async (equipmentId: number) => {
    const response = await axios.get(`${apiPowerStationForm}/teleobject-config/${equipmentId}`);
    return response.data.data;
};

export const FindAllTeleObjectsConfigsByEquipmentIdForPowerStationForm = (equipmentId: number | undefined) => {
    return useQuery(['teleObjectsConfig-power-station-form', equipmentId],
        () => {
            if (!equipmentId) {
                return [];
            }
            return fetchAllTeleObjectsConfigByEquipmentIdForPowerStationForm(equipmentId);
        }, {
        enabled: !!equipmentId,
        onError: (error) => {
            console.error('Error fetching tele objects config:', error);
        },
    });
};

const fetchEquipmentsByPowerStationIdForPowerStationForm = async (powerStationId: number) => {
    const response = await axios.get(`${apiPowerStationForm}/equipment/${powerStationId}`);
    return response.data;
};

export const FindAllEquipmentsByPowerStationIdForPowerStationFormReactQuery = (powerStationId: number | null) => {
    return useQuery(
        ['find-all-equipments-by-powerStation-id-power-station-form', powerStationId],
        () => {
            if (powerStationId !== null) {
                return fetchEquipmentsByPowerStationIdForPowerStationForm(powerStationId);
            }
            return [];
        },
        {
            enabled: powerStationId !== null
        }
    );
};

export const deleteTeleObjectForPowerStationForm = async (teleObjectId: number) => {
    const response = await axios.delete(`${apiPowerStationForm}/teleobject/${teleObjectId}`);
    return response.data;
};

export const FindAllTeleObjectsConfigsByEquipmentId = (equipmentId: number | undefined) => {
    return useQuery(['teleObjectsConfig', equipmentId],
        () => {
            if (!equipmentId) {
                // Retorna um array vazio ou um valor padrão se equipmentId for null
                return [];
            }
            return fetchAllTeleObjectsConfigByEquipmentId(equipmentId);
        }, {
        enabled: !!equipmentId, // Só executa a query se houver um equipmentId válido
        onError: (error) => {
            console.error('Error fetching tele objects config:', error);
        },
    });
};

const fetchUsersByPowerStation = async (powerStationId: number) => {
    try {
        return await axios.get<ResponseConfigurationUserAssociation>(
            `/api/power-station-user/users-with-permission/${powerStationId}`,
            {
                timeout: 30000,
            }
        ).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching users:", error);
        return Promise.reject(error);
    }
};

export const UsersAssociationReactQuery = (powerStationId: number, enabled: boolean) => {
    return useQuery(
        ["users-association", powerStationId, enabled],
        () => fetchUsersByPowerStation(powerStationId),
        {
            enabled: !!powerStationId && enabled,
            staleTime: 0,
            refetchOnMount: "always",
        }
    );
};

const savePowerStationUser = async (data: any) => {
    try {
            return await axios.post(`/api/power-station-user/save-users-to-power-station`, data).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching users data:", error);
        return Promise.reject(error);
    }
};

export const SavePowerStationUserReactQuery = () => {
    const {enqueueSnackbar} = useSnackbar();

    return useMutation(savePowerStationUser, {
        onSuccess: () => {
            enqueueSnackbar("Associação de usuários feita com sucesso.", { variant: "success", anchorOrigin: { vertical: "bottom", horizontal: "right" } });
        },
        onError: (error: any) => {
            enqueueSnackbar(`Não foi possível concluir a operação, tente novamente. ${error.message}`, {variant: "error", anchorOrigin: { vertical: "bottom", horizontal: "right" }});
        }
    });
};

const injectedEnergyLossParametersUrl = (powerStationId: number) =>
    `/api/power-station/${powerStationId}/injected-energy-loss-parameters`;

const fetchInjectedEnergyLossParameters = async (powerStationId: number): Promise<InjectedEnergyLossParameter[]> => {
    const response = await axios.get<InjectedEnergyLossParametersResponse>(
        injectedEnergyLossParametersUrl(powerStationId)
    );
    return response.data.data;
};

const injectedEnergyLossDecimalPattern = /^(0|[1-9]\d*)([.,]\d{1,6})?$/;

export const isValidInjectedEnergyLossValue = (value: string): boolean => {
    const trimmedValue = value.trim();
    return trimmedValue === "" || injectedEnergyLossDecimalPattern.test(trimmedValue);
};

export const normalizeInjectedEnergyLossValue = (value: string): string | null => {
    const trimmedValue = value.trim();
    return trimmedValue === "" ? null : trimmedValue.replace(",", ".");
};

export const InjectedEnergyLossParametersReactQuery = (powerStationId: number | null) => {
    return useQuery<InjectedEnergyLossParameter[]>({
        queryKey: ["injected-energy-loss-parameters", powerStationId],
        queryFn: () => fetchInjectedEnergyLossParameters(powerStationId as number),
        enabled: powerStationId !== null
    });
};

